import auth from '@/Data/Functions/auth';
import statusmessage from '@/Data/Objects/httpStatusCodes';

const DBCONNECTIONERROR = 'Cloud Merchant Tools could not connect to your database.',
    CITRUSSTORECONNECTIONERROR = 'Cloud Merchant Tools could not connect to your website.',
    OUTOFDATEWEBSITEERROR = 'The version of your website is not compatible with this version of Cloud Merchant Tools. Please contact the helpdesk team for resolution of this issue.';
const FATAERRORLIST = [
    DBCONNECTIONERROR,
    CITRUSSTORECONNECTIONERROR,
    OUTOFDATEWEBSITEERROR
];

const getErrorMessageTitle = (status, method) => {
    const BADREQUESTTITLE = 'Sorry, validation failed for your request',
        GENERICTITLE = 'An error occurred during the request',
        MISSINGENTITY = 'We could not find the record',
        EXISTSALREADY = 'We cannot create a record for an ID which already exists',
        NOAUTHTITLE = 'You need to logon again';
    let returnTitle = GENERICTITLE;

    if (status == 400) {
        returnTitle = BADREQUESTTITLE;
    }
    else if (status == 404 && method == 'post') {
        returnTitle = EXISTSALREADY;
    }
    else if (status == 404 && method == 'put') {
        returnTitle = MISSINGENTITY;
    }
    else if ([401, 403].includes(status)) {
        returnTitle = NOAUTHTITLE;
    }

    return returnTitle;

};

function set400ErrorResponseText(error, errorHTML) {
    let responseText = error.response.request.responseText;

    try {
        responseText = JSON.parse(error.response.request.responseText);
    }
    catch (err) {
        // The response is a string, do nothing.
    }

    if (typeof responseText == 'string') {
        errorHTML += `<p>${responseText}</p>`;
    }
    else {
        errorHTML = getErrorHTML(responseText, errorHTML);
    }

    return errorHTML;
}

/**
 * Loops the error response from a service validation error object and returns a string of HTML.
 */
function setErrorHTMLForValidationErrorResponse(responseText, erroneousFields, i, errorHTML) {
    const CURRENT_OBJECT = responseText[erroneousFields[i]];
    const CURRENT_PROPERTY = CURRENT_OBJECT.propertyName;
    const CURRENT_ERRORS = CURRENT_OBJECT.listOfErrors;

    if (CURRENT_PROPERTY.toLowerCase() !== 'generic') {
        errorHTML += `<p class='bold'> Field: ${CURRENT_PROPERTY.camelCaseToSentence()}</p>`;
    }

    /* Loop through the errors for each field */
    for (let j = 0; j < CURRENT_ERRORS.length; j++) {
        errorHTML += `<p>${CURRENT_ERRORS[j]}</p>`;
    }

    return errorHTML;
}

/**
 * Loops the error response from a EF Model error object and returns a string of HTML.
 */
function setErrorHTMLForModelErrorResponse(responseText, erroneousFields, i, errorHTML) {
    const CURRENT_ERRORS = responseText[erroneousFields[i]];

    if (erroneousFields[i].toLowerCase() !== 'generic') {
        errorHTML += `<p class='bold'> Field: ${erroneousFields[i].camelCaseToSentence()}</p>`;
    }

    /* Loop through the errors for each field */
    for (let j = 0; j < CURRENT_ERRORS.length; j++) {
        errorHTML += `<p>${CURRENT_ERRORS[j]}</p>`;
    }

    return errorHTML;
}

function getErrorHTML(responseText, errorHTML) {
    const ERRONEOUS_FIELDS = Object.keys(responseText);

    if (responseText.hasOwnProperty('errors')) {
        if (responseText.errors.hasOwnProperty('Errors')) {
            for (const error of responseText.errors.Errors) {
                errorHTML += `<p>${error}</p>`;
            }
        }
        else if (responseText.hasOwnProperty('typeOfError') && responseText.typeOfError === 'ModelValidation') {
            for (const error of Object.entries(responseText.errors)) {
                errorHTML += `<strong>Field: ${error[0]}</strong>`;

                for (const errorMessage of error[1]) {
                    errorHTML += `<p>${errorMessage}</p>`;
                }
            }
        }
    }
    else {
        /* Loop through erroneous fields */
        for (let i = 0; i < ERRONEOUS_FIELDS.length; i++) {
            if (responseText[ERRONEOUS_FIELDS[i]].hasOwnProperty('propertyName')) {
                errorHTML = setErrorHTMLForValidationErrorResponse(responseText, ERRONEOUS_FIELDS, i, errorHTML);
            }
            else {
                errorHTML = setErrorHTMLForModelErrorResponse(responseText, ERRONEOUS_FIELDS, i, errorHTML);
            }
        }
    }

    return errorHTML;
}

const RESPONSEINTERCEPTOR = (axiosInstance, vueInstance) => axiosInstance.interceptors.response.use(
    (response) => {
        if (response.headers['refreshed-token']) {
            auth.set({
                user: { token: response.headers['refreshed-token'] }
            });
        }

        return response;
    }, (error) => {
        /* Hide the pop-ups for hotkeys */
        vueInstance.hotkeyPopupVisible = false;
        vueInstance.productSearchPopupVisible = false;

        vueInstance.$root.changeSaveIndicator = false;

        let errorHTML = '';

        if (error.response) {
            errorHTML = `<h3>${getErrorMessageTitle(error.response.status, error.response.config.method)}</h3>`;

            if (error.response.config.url.toLowerCase().indexOf('/logon/logon/authenticate') > -1) {
                /* These requests handle errors slightly differently */
                return Promise.reject(error);
            }
            else if (error.response.config.url.toLowerCase().indexOf('/logon/logon/2fa') > -1) {
                /* We handle 2FA errors in the UI */
                return Promise.reject(error);
            }
            else {
                if (error.response.status === 400) {
                    if (error.response.config.headers.Ignore400Errors === 'true') {
                        return Promise.reject(error);
                    }
                    else {
                        errorHTML = set400ErrorResponseText(error, errorHTML);
                    }
                }
                else if (error.response.status == 404 && error.response.config.headers.SuccessOn404 === true) {
                    return Promise.resolve({ data: null });
                }
                else if (error.response.status == 404 && error.response.config.headers.Ignore404Errors == 'true') {
                    // Handle this response inside a catch on the request, rather than in the global handler.
                    return Promise.reject(error);
                }
                else if ([401, 403].includes(error.response.status) && error.response.config.headers.Ignore401Errors == 'true') {
                    // Handle this response inside a catch on the request, rather than in the global handler.
                    return Promise.reject(error);
                }
                else if ([401, 403].includes(error.response.status) || (error.response.status == 500 && error.response.data?.Message?.toLowerCase().includes('could not find the authenticated user and so could not retrieve the appropriate connection string'))) {
                    errorHTML += '<p class="bold">Unauthorized</p><p>You have been logged out, please click OK below to return to the login page..</p>';

                    auth.remove();

                    const AUTO_TIMER = setTimeout(() => {
                        vueInstance.$router.go();
                    }, 25000);

                    UIkit.modal.alert(errorHTML, {
                        'esc-close': false,
                        'bg-close': false
                    })
                        .then(() => {
                            clearTimeout(AUTO_TIMER);
                            vueInstance.$router.go();
                        });

                    return Promise.reject(error);
                }
                else if (error.response.status == 403) {
                    errorHTML += '<p class="bold">Permission Denied</p>';

                    if (error.response.data) {
                        errorHTML += `<p>${error.response.data}</p>`;
                    }
                }
                else if (error.response.status == 502) {
                    errorHTML += '<strong>Bad Gateway</strong><p>An error has caused the connection to time out. Refresh the page, to make another attempt.</p><p>If this issue persists, please contact a member of the Citrus Lime support team.</p>';
                }
                else {
                    const MESSAGE_FROM_CODE = statusmessage.getMessage(error.response.status);

                    errorHTML += `<p class='bold'>${MESSAGE_FROM_CODE['technicalMessage']}</p>`;

                    if (FATAERRORLIST.includes(error.response.data.Message)) {
                        errorHTML += `<p>${error.response.data.Message}</p>`;

                        setFatalErrorVueValues(vueInstance, error);
                        vueInstance.$router.push({ path: '/fatal' });

                        return Promise.reject(error);
                    }
                    else if (typeof error.response.data == 'string'
                        &&
                        error.response.data.length > 0
                        &&
                        error.response.status !== 404) {
                        errorHTML += `<p>Error: ${error.response.data}</p>`;

                    }
                    else {
                        errorHTML += `<p>${MESSAGE_FROM_CODE['customerFacingMessage']}</p>`;
                    }
                }
            }
        }
        else {
            const MESSAGE_FROM_CODE = statusmessage.getMessage(0);

            errorHTML += `<p class='bold'>${MESSAGE_FROM_CODE['technicalMessage']}</p><p>${MESSAGE_FROM_CODE['customerFacingMessage']}</p>`;
        }

        UIkit.modal.alert(errorHTML, {
            stack: true
        });

        if (vueInstance.showPageLoad) {
            vueInstance.showPageLoad(false);
        }

        return Promise.reject(error);
    });
const REQUESTINTERCEPTOR = (axiosInstance) => axiosInstance.interceptors.request.use((config) => {
    if (auth.status() === false) {
        config.headers.Authorization = `Bearer ${auth.get2FA()}`;
    }
    else {
        config.headers.Authorization = `Bearer ${auth.get()}`;
    }

    return config;
});
const INTERCEPTORS = {
    response: RESPONSEINTERCEPTOR,
    request: REQUESTINTERCEPTOR
};

export default INTERCEPTORS;

function setFatalErrorVueValues(vueInstance, error) {
    vueInstance.$root.fatalError.appHasFatalError = true;
    vueInstance.$root.fatalError.endpoint = error.response.config.url.toLowerCase();
    vueInstance.$root.fatalError.message = error.response.data.Message;
}
